import { createPortal } from "react-dom";
import { useEffect, type ReactNode, type HTMLAttributes } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Drawer lateral derecho — para vistas de detalle.
 * Similar al Dialog pero se desliza desde la derecha.
 */
export function Drawer({
  open,
  onOpenChange,
  children,
  size = "md",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  size?: "sm" | "md" | "lg";
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  const widths = { sm: "w-full max-w-sm", md: "w-full max-w-lg", lg: "w-full max-w-2xl" };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/50 animate-in fade-in"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div
        className={cn(
          "relative flex h-full flex-col border-l bg-card text-card-foreground shadow-xl",
          widths[size]
        )}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground max-md:top-[calc(0.75rem+env(safe-area-inset-top))] max-md:p-2"
          aria-label="Cerrar"
        >
          <X size={16} />
        </button>
        {children}
      </div>
    </div>,
    document.body
  );
}

export function DrawerHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  // rc136 — móvil: debajo de la barra de estado del iPhone.
  return <div className={cn("border-b p-4 pr-10 max-md:pt-[calc(1rem+env(safe-area-inset-top))]", className)} {...props} />;
}

export function DrawerTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn("text-base font-semibold leading-none", className)} {...props} />;
}

export function DrawerDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-xs text-muted-foreground mt-1", className)} {...props} />;
}

export function DrawerBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex-1 overflow-y-auto p-4 max-md:pb-[calc(1rem+env(safe-area-inset-bottom))]", className)} {...props} />;
}

export function DrawerFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex items-center justify-end gap-2 border-t bg-muted/30 p-3 max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom))]", className)}
      {...props}
    />
  );
}
